RivalIQ.apps.Dashboard.module('widgets', function (widgets, App, Backbone, Marionette, $, _) {

    'use strict';

    function getTimezoneString(refDate, timezone) {
        if (timezone) {
            return ` ${moment.tz(refDate, timezone).zoneName()}`;
        } else {
            return '';
        }
    }

    function formatAsOfPeriodEnd(timePeriod, timezone) {
        return `As of ${timePeriod.mainPeriodEnd.format('MMM D, YYYY')
        } vs. ${timePeriod.comparePeriodEnd.format('MMM D, YYYY')}${getTimezoneString(timePeriod.comparePeriodEnd, timezone)}`;
    }

    function formatMainPeriodInterval(timePeriod, timezone) {
        return `${timePeriod.mainPeriodStart.format('MMM D, YYYY')} - ${
            timePeriod.mainPeriodEnd.format('MMM D, YYYY')}${getTimezoneString(timePeriod.mainPeriodEnd, timezone)}`;
    }

    function formatVsIntervals(timePeriod, timezone) {
        return `${timePeriod.mainPeriodStart.format('MMM D, YYYY')} - ${
            timePeriod.mainPeriodEnd.format('MMM D, YYYY')
        } vs. ${timePeriod.comparePeriodStart.format('MMM D, YYYY')} - ${
            timePeriod.comparePeriodEnd.format('MMM D, YYYY')}${getTimezoneString(timePeriod.comparePeriodEnd, timezone)}`;
    }

    const curations = RivalIQ.enums.curation;

    function isInEditor(curationId) {
        return _.includes([
            curations.DASHBOARDS_WIDGET_EDIT,
            curations.DASHBOARDS_BUILDER
        ], curationId);
    }

    // TODO this needs to be more generic
    const Panel = App.views.Panel.extend({
        isInEditor: function () {
            const me = this;
            return isInEditor(me.dashboardModel.curation.id);
        },
        isInDashboard: function () {
            const me = this;
            return (me.dashboardModel.curation.id === curations.DASHBOARDS);
        },
        showActions: function () {
            const me = this;

            if (me.isInEditor()) {
                return;
            }

            if (!me.dashboardModel.hideFavoritesActions) {
                if (me.isInDashboard()) {
                    me.showEditActions();
                }
            }

            if (!me.dashboardModel.isFocus()) {
                me.showExportDropdown();
            }
        },
        hasFooter: function () {
            const me = this;
            return me.dashboardModel.hasFooter();
        }
    });

    function accessibleMetrics(props, metrics) {
        return _.filter(metrics, function (metric) {
            return RivalIQ.metric.access.channel(metric, props);
        });
    }

    // TODO this should be named `Chart`, but otherwise clashes with the view
    // in `widgets/chart.js`...
    widgets.ChartWidget = RivalIQ.apps.Dashboard.models.Widget.mixin({
        panelView: Panel,
        detailPanelView: Panel,

        mixins: {
            focusCompanyTitlePlacementMixin: App.mixins.FocusCompanyTitlePlacementMixin
        },

        title: function () {
            const me = this;
            return me.getProp('title') || me.getChartTitle();
        },

        description: function () {
            const me = this;

            return me.getProp('description') || RivalIQ.metric.render.description(me.metrics, me.descriptionOptions);
        },

        getChartTitle: function (dimensions) {
            const me = this;
            dimensions = dimensions || ['channel', 'timeAnalysisMode'];
            return RivalIQ.metric.names.metricName(me.metrics, {
                dimensions: dimensions
            });
        },

        maxDimensionCount: _.constant(1),

        buildMetrics: function (props, metrics) {
            const me = this;

            metrics = accessibleMetrics(props, metrics || props.metrics);

            if (_.isEmpty(metrics)) {
                return [];
            } else {
                metrics = RivalIQ.metric.build(props, metrics);
                return RivalIQ.metric.pin(metrics, me.maxDimensionCount(metrics));
            }
        },

        shouldShowChannelWall: function () {
            const me = this;

            const props = me.getProps();
            const metrics = _.reject(me.metrics, function (metric) {
                return RivalIQ.metric.access.token(metric, props);
            });

            if (!metrics.length) {
                return false;
            }

            return metrics[0].channel;
        },

        shouldShowPlanWall: function () {
            const me = this;

            const plan = me.getProp('plan');
            return !_.every(me.metrics, function (metric) {
                return RivalIQ.metric.access.plan(metric, plan);
            });
        },

        propsToSerialize: [],
        // Do not serialize companyId so that we always show the focus company
        propsToOmitFromSerialization: ['companyId'],

        getPropsForSerialization: function (props) {
            const me = this;

            const extensions = _.pick(me.getProps(), me.propsToSerialize.concat(['metrics', 'columnWidth', 'showBenchmarkLine', 'selectedBenchmarkCompareAgainst']));
            return _.mergeEnumerable(_.omit(_.defaults(props, extensions), me.propsToOmitFromSerialization));
        },

        getExportSubtitle: function () {
            const me = this;
            if (me.isFocus()) {
                const timezone = RivalIQ.state.landscape.get('timezone');
                return me.getTimePeriodDisplayString(me.getProp('timePeriod'), timezone);
            }
        },

        // adapted from analyze/models/graph.js
        getTimePeriodDisplayString: function (timePeriod, timezone) {
            const me = this;

            if (!timePeriod) {
                return RivalIQ.utils.timePeriod.getAsOfDisplayString();
            }

            timePeriod = _.clone(timePeriod);
            RivalIQ.utils.timePeriod.rehydrate(timePeriod);

            // NOTE: important to use me.getProp('metrics') here instead of me.metrics as the latter can contain
            // additional metrics (such as `name` for the metric_table) that we don't want to look at.
            const metricProps = _.reduce(me.getProp('metrics'), function (memo, metric) {
                const wrapped = RivalIQ.metric(metric);

                memo.metricTypes[wrapped.getMetricType()] = true;
                if (!_.isUndefined(wrapped.timeAnalysisMode)) {
                    memo.timeAnalysisModes[wrapped.timeAnalysisMode] = true;
                }
                memo.hasInterval = memo.hasInterval || Boolean(RivalIQ.metric.timePeriod.getInterval(metric));

                return memo;
            }, {
                metricTypes: {},
                timeAnalysisModes: {},
                hasInterval: false
            });

            // If this is a focus table widget, we'll be showing change metric as one of the columns, hence the widgetId check
            const hasCurrentChangeMetric = me.widgetId === RivalIQ.enums.widgetTypes.COMPANY_TABLE;

            // If this is a bar chart + change widget, we'll be showing change metric as one of the columns, hence the widgetId check.
            const hasDeltaMetric = (me.widgetId === RivalIQ.enums.widgetTypes.COMPANY_METRIC_SUMMARY ||
                me.widgetId === RivalIQ.enums.widgetTypes.METRIC_BAR_CHART_WITH_CHANGE ||
                metricProps.timeAnalysisModes[RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE] ||
                metricProps.timeAnalysisModes[RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE]);
            const hasInterval = metricProps.hasInterval;
            const hasVolumetric = Boolean(metricProps.metricTypes[RivalIQ.metrics.metricType.VOLUME]);

            if (hasCurrentChangeMetric) {
                return formatAsOfPeriodEnd(timePeriod, timezone);
            } else if (hasDeltaMetric) {
                if (hasVolumetric) {
                    return formatVsIntervals(timePeriod, timezone);
                } else {
                    return formatAsOfPeriodEnd(timePeriod, timezone);
                }
            } else if (hasInterval) {
                return formatMainPeriodInterval(timePeriod, timezone);
            } else {
                if (hasVolumetric) {
                    return formatMainPeriodInterval(timePeriod, timezone);
                }

                const hasMetricsWithHistory = _.some(me.metrics, RivalIQ.metric.graph.hasHistory);

                if (hasMetricsWithHistory) {
                    return RivalIQ.utils.timePeriod.getAsOfDisplayString(timePeriod, timezone);
                } else {
                    return RivalIQ.utils.timePeriod.getAsOfDisplayString();
                }
            }
        },

        exportFormats: [
            RivalIQ.enums.exportFormat.PPT,
            RivalIQ.enums.exportFormat.PDF,
            RivalIQ.enums.exportFormat.PNG,
            RivalIQ.enums.exportFormat.CSV
        ],

        propValues: {
            timeAnalysisMode: [
                RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD,
                RivalIQ.enums.timeAnalysisMode.DAILY_VALUES,
                RivalIQ.enums.timeAnalysisMode.COMPARE_PERIOD,
                RivalIQ.enums.timeAnalysisMode.DELTA_ABSOLUTE,
                RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE
            ]
        },

        csvExportProps: function () {
            const me = this;
            const collection = new RivalIQ.collections.MetricDefinitions(me.metrics);

            return {
                type: 'metric',
                wideRow: true,
                nameDimensions: ['channel', 'timeAnalysisMode'],
                metric: collection.invoke('serialize')
            };
        },

        filter: function () {
            const me = this;

            if (me.neverFilter) {
                return false;
            }

            const props = me.getProps();

            // only filter if, after removing all the hidden metrics, the chart is invalid.
            props.metrics = accessibleMetrics(props, props.metrics);
            return !isInEditor(me.curation.id) && !me.isSupported(props);
        },

        getSlideDescriptions: function () {
            const me = this;

            return _.map(me.metrics, function (metric) {
                return {
                    name: RivalIQ.metric.names.metricName(metric, {
                        dimensions: ['channel']
                    }),
                    description: RivalIQ.metric.search.getPropertyValue(metric, 'description')
                };
            });
        },

        exportProps: function (props) {
            const me = this;

            if (me.propsToSerializeForExport) {
                props = _.pick(props, me.propsToSerializeForExport.concat(me.defaultExportProps));
            }

            return props;
        },

        // Props that should be reflected in full page export
        getPropsForCurationExport: function () {
            const me = this;
            return _.pick(me.getProps(), 'inclusions', 'exclusions', 'sortOrder', 'orderBy');
        }
    });

    widgets.ChartWidget.prepare = function (props) {
        const privateMetrics = _.filter(props.metrics, metric => {
            const source = RivalIQ.metric.property.source(metric);
            return _.get(RivalIQ.enums.sourceDetails, [source, 'privateData']);
        });

        if (!_.size(privateMetrics)) {
            return $.when();
        }

        // fetch private metadata for NEH popovers
        const fetches = _.map(privateMetrics, (metric) => {
            const source = RivalIQ.metric.property.source(metric);
            return RivalIQ.data.privateMetadata.fetch(_.defaults({
                channel: RivalIQ.enums.sourceDetails[source].channel,
                source: source
            }, props));
        });

        return $.when.apply($, fetches)
            .then(function () {
                return _.merge.apply(null, arguments);
            });
    };

});
